import { Icon, mergeStyleSets } from "@fluentui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import {
  COVERAGE_FIELDS,
  formatRequestedLine,
  formatWindscreen,
  parseCoverages,
  parseRequestedLines,
  REQUESTED_LINE_FIELDS,
} from "./FinanceForm/requestedLinesHelpers";

const getClassNames = (theme: IStyledTheme, isMarked: boolean) =>
  mergeStyleSets({
    // Prominent callout so an internal reviewer immediately sees what the
    // client requested (both in the read-only preview and while confirming the
    // vehicle / entering premiums in edit mode). Yellow (like the other marked
    // fields) when the client changed the request.
    requestedBox: {
      margin: "16px 35px 8px 50px",
      padding: "12px 16px",
      borderRadius: "4px",
      background: isMarked
        ? theme.palette.yellowLight
        : theme.palette.themeLighterAlt,
      borderLeft: `4px solid ${theme.palette.themePrimary}`,
      ...(isMarked && { color: "black" }),
    },
    requestedTitle: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: 700,
      fontSize: "15px",
      color: theme.palette.themePrimary,
      marginBottom: "6px",
    },
    requestedRow: {
      fontSize: "14px",
      color: isMarked ? "black" : theme.palette.black,
      marginTop: "4px",
    },
    requestedRowLabel: {
      fontWeight: 600,
      marginRight: "6px",
    },
    requestedRowValue: {
      fontWeight: 600,
      color: theme.palette.themeDarker,
    },
  });

// Read-only summary of the insurance lines + additional coverages an AT client
// requested (stored in `clientCoveragesInfo`). Renders nothing for vehicles
// without a client offer.
const RequestedOfferSummary = ({ vehicle }: { vehicle: any }) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const isMarked = !!vehicle?.fieldsUpdatedByExternalUser?.includes(
    "clientCoveragesInfo"
  );
  const classes = getClassNames(theme, isMarked);

  let isOffer = false;
  try {
    isOffer = !!JSON.parse(vehicle?.clientCoveragesInfo || "null")
      ?.requestedLines;
  } catch {
    isOffer = false;
  }
  if (!isOffer) return null;

  const lines = parseRequestedLines(vehicle?.clientCoveragesInfo);
  const requestedLineLabels = REQUESTED_LINE_FIELDS.filter(
    (f) => lines[f.key]
  ).map((f) => formatRequestedLine(lines, f.key, t));
  // CZ: windscreen coverage, after the lines
  const windscreen = formatWindscreen(lines, t);
  if (windscreen) requestedLineLabels.push(windscreen);

  const selectedCoverages = parseCoverages(vehicle?.clientCoveragesInfo);
  const coverages = COVERAGE_FIELDS.filter(
    (f) => selectedCoverages[f.key]
  ).map((f) => t(f.labelKey));

  return (
    <div className={classes.requestedBox}>
      <div className={classes.requestedTitle}>
        <Icon iconName="ClipboardList" />
        {t("bfm.vehicleForm.requestedLines.label")}
      </div>
      <div className={classes.requestedRow}>
        <span className={classes.requestedRowValue}>
          {requestedLineLabels.join(", ")}
        </span>
      </div>
      {coverages.length > 0 && (
        <div className={classes.requestedRow}>
          <span className={classes.requestedRowLabel}>
            {t("bfm.vehicleForm.additionalCoverages.label")}:
          </span>
          <span className={classes.requestedRowValue}>
            {coverages.join(", ")}
          </span>
        </div>
      )}
    </div>
  );
};

export default RequestedOfferSummary;
